import { logger } from "@octopusdeploy/logging";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { type ReactNode, useCallback, useState } from "react";
import { v4 } from "uuid";
import type { UnknownPageRegistration } from "~/routing/pageRegistrations/PageRegistration";
import { ActiveWorkflow } from "~/workflows/ActiveWorkflow";
import type { StartWorkflowContext } from "~/workflows/StartWorkflowContext";
import { StartWorkflowProvider } from "~/workflows/StartWorkflowContext";
import { createWorkflowStartedEvent } from "~/workflows/analytics/WorkflowStartedEvent";
import type { CompleteWorkflow, UnknownWorkflowRegistration, WorkflowRegistration } from "~/workflows/workflowRegistrations";
import type { Workflow } from "~/workflows/workflows";
export function ActiveWorkflowsProvider({ workflowRegistrations, children }: {
    workflowRegistrations: UnknownWorkflowRegistration[];
    children: ReactNode;
}) {
    const [activeWorkflowRenderers, setActiveWorkflowRenderers] = useState<ActiveWorkflowRenderer[]>([]);
    const trackEvent = useTrackEvent();
    const onWorkflowExited = useCallback((workflowInstanceId: string) => {
        // We don't call this when the workflow completes or cancels, because it still needs to animate away.
        // Instead, we only remove the active workflow when it has completely exited and any animations have completed.
        setActiveWorkflowRenderers((prev) => prev.filter((w) => w.workflowInstanceId !== workflowInstanceId));
    }, []);
    const startWorkflow = useCallback<StartWorkflowContext>(<Input, Output>(input: Input, workflow: Workflow<Input, Output>, onWorkflowCompleted: CompleteWorkflow<Output>, abortSignal: AbortSignal, pageRegistration: UnknownPageRegistration | null) => {
        const workflowRegistration = getWorkflowRegistrationForWorkflow(workflow, workflowRegistrations);
        if (workflowRegistration === null) {
            logger.error("No workflow registration found for {workflowId}", { workflowId: workflow.workflowId });
            // Returning here means the interaction will have appeared to do nothing (e.g., a button click that does nothing).
            return null;
        }
        const workflowInstanceId = v4();
        const activeWorkflowReactNode = createActiveWorkflow(input, workflowRegistration, onWorkflowCompleted, onWorkflowExited, workflowInstanceId, pageRegistration, abortSignal);
        setActiveWorkflowRenderers((prev) => [...prev, activeWorkflowReactNode]);
        trackEvent(createWorkflowStartedEvent({
            workflow,
            workflowInstanceId,
            pageRegistration,
        }));
    }, [onWorkflowExited, trackEvent, workflowRegistrations]);
    return (<StartWorkflowProvider value={startWorkflow}>
            {activeWorkflowRenderers.map((n) => n.render())}
            {children}
        </StartWorkflowProvider>);
}
// This is a bit like a component, but not quite. If this was a component, it would need to be generic,
// but the important part is that this is not generic since we need to store a collection of these with different generic types
interface ActiveWorkflowRenderer {
    workflowInstanceId: string;
    render: () => ReactNode;
}
function createActiveWorkflow<Input, Output, LoaderData>(input: Input, workflowRegistration: WorkflowRegistration<Input, Output, LoaderData>, onWorkflowCompleted: CompleteWorkflow<Output>, onWorkflowExited: (workflowInstanceId: string) => void, workflowInstanceId: string, pageRegistration: UnknownPageRegistration | null, abortSignal: AbortSignal): ActiveWorkflowRenderer {
    return {
        render: () => (<ActiveWorkflow<Input, Output, LoaderData> key={workflowInstanceId} workflowRegistration={workflowRegistration} workflowInstanceId={workflowInstanceId} input={input} onWorkflowCompleted={onWorkflowCompleted} onWorkflowExited={onWorkflowExited} pageRegistration={pageRegistration} abortSignal={abortSignal}/>),
        workflowInstanceId,
    };
}
export function getWorkflowRegistrationForWorkflow<Input, Output, LoaderData>(workflow: Workflow<Input, Output>, workflowRegistrations: UnknownWorkflowRegistration[]): WorkflowRegistration<Input, Output, LoaderData> | null {
    const workflowRegistration = Object.values(workflowRegistrations).find((w) => w.workflow.workflowId === workflow.workflowId);
    if (workflowRegistration === undefined) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return workflowRegistration as unknown as WorkflowRegistration<Input, Output, LoaderData>;
}
